import React, { useState, useEffect, useMemo, memo } from 'react';
import JSZip from 'jszip';
import { readXlsxSheetAsRows } from '../../../../lib/xlsxUtils';
import { parseCSV } from '../../../../lib/csvUtils';
import { Tabs, Table, Typography } from '../../../../components';

const { Title } = Typography;

const SheetDataViewerComponent = React.memo(({
    file,
    requiredCsvFiles = [],
    excelSheetsToDisplay = [],
    setStep,
    emptyStateText = 'No data found',
}) => {
    const [sheetData, setSheetData] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(null);

    useEffect(() => {
        const parseFile = async () => {
            if (!file) return;

            setLoading(true);
            try {
                const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
                const parsedData = isExcel ? await parseExcelFile(file) : await parseZipCSVFile(file);

                setSheetData(parsedData);
                // Set the first available sheet as the active tab
                const firstSheet = Object.keys(parsedData)[0];
                if (firstSheet) {
                    setActiveTab(firstSheet);
                }
            } catch (error) {
                console.error('Error parsing file:', error);
            } finally {
                // Add a bit of delay for better UX when parsing is fast
                setTimeout(() => {
                    setStep(2);
                    setLoading(false);
                }, 1000);
            }
        };

        parseFile();
    }, [file]);

    const parseZipCSVFile = async (zipFile) => {
        const zip = await JSZip.loadAsync(zipFile);
        const parsedData = {};

        for (const fileName of requiredCsvFiles) {
            const zipEntry = Object.values(zip.files).find((f) => f.name.endsWith(fileName));
            if (zipEntry) {
                const csvContent = await zipEntry.async('text');
                parsedData[fileName] = parseCSV(csvContent);
            }
        }
        return parsedData;
    };

    const parseExcelFile = async (excelFile) => {
        const parsedData = {};

        for (const sheetName of excelSheetsToDisplay) {
            const rows = await readXlsxSheetAsRows(excelFile, sheetName);
            if (!rows) continue;

            const csvString = rows
                .map((row) =>
                    row
                        .map((cell) => {
                            const val = String(cell ?? '');
                            return val.includes(',') || val.includes('"') || val.includes('\n')
                                ? `"${val.replace(/"/g, '""')}"`
                                : val;
                        })
                        .join(','),
                )
                .join('\n');
            parsedData[sheetName] = parseCSV(csvString);
        }
        return parsedData;
    };

    // Memoize tab items to avoid unnecessary recalculations
    const tabItems = useMemo(() => {
        return Object.entries(sheetData).map(([sheetName, { columns, data }]) => {
            // Calculate total width for horizontal scroll
            const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);
            return {
                key: sheetName,
                label: sheetName,
                children: (
                    <Table
                        columns={columns}
                        dataSource={data}
                        scroll={{ x: totalWidth, y: 500 }}
                        pagination={false}
                        size="small"
                        tableLayout="fixed"
                        virtual
                    />
                ),
            };
        });
    }, [sheetData]);

    if (loading) return;

    if (Object.keys(sheetData).length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={4}>{emptyStateText}</Title>
            </div>
        );
    }

    return (
        <div className="csv-data-viewer">
            <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" items={tabItems} />
        </div>
    );
});

// Memoize the entire component so it only re-renders when props change
export const SheetDataViewer = memo(SheetDataViewerComponent);
